/**
 * Per-device toggle for non-speech audio — data sonification, event cues, any
 * sound an app makes that is not speech (STEM-214).
 *
 * ## Default off, deliberately
 *
 * Sound is an **accommodation, not a default experience**: a classroom of
 * thirty laptops all chirping is its own accessibility problem. A student who
 * wants it turns it on once and has it remembered.
 *
 * ## One key for every app, deliberately
 *
 * Three apps each had their own copy of this with their own storage key
 * (`graph-builder:sonification`, `pendulum-v2:audioCues`,
 * `pendulum-library:audioCues`), so a student who needed sound had to find and
 * enable it again in every activity they opened. The preference is a fact about
 * the person and their device, not about which activity is on screen — so it is
 * stored once, under one key, and every app reads it.
 *
 * The consequence is intended: enabling sound in one activity enables it in the
 * next. The alternative — re-enabling per app — is friction paid exclusively by
 * the students who need the accommodation.
 *
 * ## Module state, not `useState`
 *
 * The control and the consumer usually have no common ancestor (a toggle in the
 * sidebar, a canvas that sounds). Per-component state would let two consumers
 * disagree, and the app that had it worked only because it happened to have
 * exactly one consumer that both toggled and read the value — a latent break
 * for whoever added the second. A context would work too, but this is one
 * boolean and the subscription is three lines.
 *
 * ## Why it lives in the kit rather than in `@alchemiesolns/audio`
 *
 * The audio package is deliberately framework-agnostic — `@alchemiesolns/palette`
 * depends on it and uses no React at all — so putting a React hook on its barrel
 * would drag React into a package that has carefully avoided it. The kit already
 * owns the identical sibling (`useAccessibilityOptions`: same module state, same
 * listener set, same `_reset` for tests), so this sits beside it and imports the
 * one audio function it needs.
 */
import { useCallback, useEffect, useState } from 'react';
import { resumeAudioContext } from '@alchemiesolns/audio';

const STORAGE_KEY = 'alchemie:sound';

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    // Private browsing and blocked storage both throw; silence is the safe
    // default, not a crash on mount.
    return false;
  }
}

let enabled = readStored();
const listeners = new Set<(next: boolean) => void>();

function setEnabledGlobal(next: boolean) {
  enabled = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
  } catch {
    // Not persisting is survivable; the toggle still works for this session.
  }
  // Wake the context on enable, so the first sound after toggling plays
  // immediately rather than waiting for the next user gesture (Safari/iOS
  // suspend it). Safe to call repeatedly. This is why the hook lives in the
  // audio package: it must resume *the* context the app's sound plays on, and
  // an app keeping a second, local AudioContext would have this resume one
  // while it plays on the other.
  if (next) resumeAudioContext();
  for (const listener of listeners) listener(next);
}

/**
 * Test-only: reset to the default (off) and clear the stored value, so cases
 * don't leak the module-level flag between each other. Mirrors
 * `_resetAccessibilityOptions` in the kit.
 */
export function _resetSoundPreference(): void {
  enabled = false;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — nothing to clear
  }
  for (const listener of listeners) listener(false);
}

export interface UseSoundPreferenceResult {
  readonly enabled: boolean;
  readonly setEnabled: (next: boolean) => void;
}

export function useSoundPreference(): UseSoundPreferenceResult {
  const [value, setValue] = useState(enabled);

  useEffect(() => {
    listeners.add(setValue);
    // A second consumer mounting after the first toggled would otherwise
    // render the stale initial value.
    setValue(enabled);
    return () => {
      listeners.delete(setValue);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => setEnabledGlobal(next), []);

  return { enabled: value, setEnabled };
}
